import { useEffect, useState } from "react";
import { api, API_BASE, PRIORITIES } from "@/lib/api";
import { useStatuses } from "@/lib/useStatuses";
import { PeriodPicker, defaultPeriod } from "@/components/PeriodPicker";
import { PageHeader, Th } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, FileDown, Printer, Loader2 } from "lucide-react";

function FSel({ value, onChange, items, label, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white dark:bg-slate-900"><SelectItem value="all">{label}</SelectItem>{items.map((s) => <SelectItem key={s.id || s} value={s.id || s}>{s.name || s}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function Reports() {
  const { active: statusList } = useStatuses();
  const [list, setList] = useState([]);
  const [kind, setKind] = useState("period");
  const [period, setPeriod] = useState(defaultPeriod());
  const [f, setF] = useState({ queue_id: "all", assigned_to: "all", sector_id: "all", os_type_id: "all", status: "all", priority: "all" });
  const [cat, setCat] = useState({ queues: [], users: [], sectors: [], types: [] });
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/reports").then((r) => setList(r.data));
    Promise.all([api.get("/queues"), api.get("/users"), api.get("/sectors"), api.get("/os-types")]).then(([q, u, s, t]) => setCat({ queues: q.data, users: u.data, sectors: s.data, types: t.data }));
  }, []);

  const params = () => ({ date_from: period.from, date_to: period.to, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all")) });

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/${kind}`, { params: params() }).then((r) => setRep(r.data)).finally(() => setLoading(false));
  }, [kind, period, f]); // eslint-disable-line

  const exportAs = async (fmt) => {
    try {
      const r = await api.get(`/reports/${kind}/export`, { params: { ...params(), fmt }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `relatorio_${kind}_${period.from}_${period.to}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado em ${fmt.toUpperCase()}`);
    } catch { toast.error("Erro ao exportar"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 print:p-0">
      <div className="print:hidden">
        <PageHeader title="Relatórios" subtitle="Visualize, imprima e exporte em PDF, Excel ou CSV.">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="report-print-btn"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
            <Button variant="outline" size="sm" onClick={() => exportAs("pdf")} data-testid="report-pdf-btn"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => exportAs("xlsx")} data-testid="report-xlsx-btn"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
            <Button variant="outline" size="sm" onClick={() => exportAs("csv")} data-testid="report-csv-btn"><FileDown className="w-4 h-4 mr-1" /> CSV</Button>
          </div>
        </PageHeader>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-3 p-3 border-slate-200 dark:border-slate-800 print:hidden h-fit">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-2 pb-2">Relatórios</p>
          {list.map((r) => (
            <button key={r.key} onClick={() => setKind(r.key)} data-testid={`report-${r.key}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${kind === r.key ? "bg-emerald-500/10 text-emerald-700 font-semibold" : "text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>{r.name}</button>
          ))}
        </Card>

        <div className="lg:col-span-9 space-y-4">
          <Card className="p-4 border-slate-200 dark:border-slate-800 space-y-3 print:hidden">
            <PeriodPicker value={period} onApply={setPeriod} />
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
              <FSel value={f.queue_id} onChange={(v) => setF({ ...f, queue_id: v })} items={cat.queues} label="Todas as filas" testId="rep-queue-filter" />
              <FSel value={f.assigned_to} onChange={(v) => setF({ ...f, assigned_to: v })} items={cat.users} label="Todos usuários" testId="rep-user-filter" />
              <FSel value={f.sector_id} onChange={(v) => setF({ ...f, sector_id: v })} items={cat.sectors} label="Todos setores" testId="rep-sector-filter" />
              <FSel value={f.os_type_id} onChange={(v) => setF({ ...f, os_type_id: v })} items={cat.types} label="Todos tipos" testId="rep-type-filter" />
              <FSel value={f.status} onChange={(v) => setF({ ...f, status: v })} items={statusList.map((s) => s.name)} label="Todos status" testId="rep-status-filter" />
              <FSel value={f.priority} onChange={(v) => setF({ ...f, priority: v })} items={PRIORITIES} label="Todas prioridades" testId="rep-priority-filter" />
            </div>
          </Card>

          <Card className="border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="report-table">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-2">
              <div><h3 className="font-bold text-slate-900 dark:text-white">{rep?.title}</h3><p className="text-xs text-slate-500">Período: {period.from} a {period.to}</p></div>
              <div className="flex gap-3 flex-wrap">{rep && Object.entries(rep.summary).map(([k, v]) => (
                <div key={k} className="text-right"><p className="text-[10px] uppercase text-slate-400">{k}</p><p className="font-extrabold text-slate-900 dark:text-white">{v}</p></div>))}</div>
            </div>
            <div className="overflow-x-auto max-h-[60vh]">
              {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900 sticky top-0"><tr>{rep?.columns.map((c) => <Th key={c}>{c}</Th>)}</tr></thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {rep?.rows.length === 0 && <tr><td colSpan={rep.columns.length} className="text-center py-10 text-slate-500">Nenhum dado no período.</td></tr>}
                    {rep?.rows.map((r, i) => <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">{rep.columns.map((c) => <td key={c} className="px-4 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">{String(r[c] ?? "")}</td>)}</tr>)}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
